import { connect } from 'formik';
import React, { ChangeEvent, Component, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { RegisterElement } from '..';
import { getDescribedBy } from '../Input/inputUtils';
import { FieldHelpersWrapper, GlyphiconWrapper, wrapperClasses } from '../utils/formHelpers';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import { sizeStyles } from '../utils/sizes';
import { IInputGroupProps, InnerInputGroupProps } from './types';

interface IButtonWrapperProps {
  prepend?: boolean;
}

const AddonWrapper = styled.div`
  &&& {
    background-color: #eeeeee;
  }
`;

const ButtonsWrapper = styled.div<IButtonWrapperProps>`
  .btn {
    &:last-child {
      margin-right: ${(p) => p.prepend && '-2px'};
    }
  }
`;

export class InputGroup extends Component<PropsWithChildren<InnerInputGroupProps>> {
  private id = this.props.id || createId('input');

  public componentDidMount() {
    this.props.formik.registerField && this.props.formik.registerField(this.props.name, this);
  }
  public componentWillUnmount() {
    this.props.formik.unregisterField && this.props.formik.unregisterField(this.props.name);
  }

  public render() {
    const { id } = this;
    const {
      appendAddon,
      appendButtons,
      formik,
      onClick,
      name = '',
      prependAddon,
      prependButtons,
      helpText,
      displaySuccess = false,
      onChange,
      validate,
      required,
      label,
      sizePreset,
      ...inputAttrsProps
    } = this.props;
    const { valid = true, touched = false, value = '', onBlur } = resolveProps(this.props, this.props.formik);

    const showError = touched && !valid;
    const showSuccess = displaySuccess && touched && valid;
    const describedBy = getDescribedBy({
      hasErrors: showError,
      hasHelpText: !!helpText,
      id,
    });
    const containerClasses = wrapperClasses({
      className: 'hlc-input-wrapper',
      showError,
      showSuccess,
    });

    return (
      <RegisterElement name={`register-${id}`}>
        {(ref: any) => {
          const myRef = showError ? ref : undefined;
          return (
            <div
              ref={myRef}
              id={`${id}-wrapper`}
              className={containerClasses}
              qa-id={this.props['qa-id']}
              style={sizeStyles(sizePreset)}
            >
              <FieldHelpersWrapper id={id} props={this.props}>
                <div className="input-group">
                  {prependButtons && (
                    <ButtonsWrapper prepend={true} className="input-group-btn">
                      {prependButtons.map((addon: any, idx: number) => React.cloneElement(addon, { key: idx }))}
                    </ButtonsWrapper>
                  )}

                  {prependAddon && <AddonWrapper className="input-group-addon">{prependAddon}</AddonWrapper>}

                  <GlyphiconWrapper {...{ showError, showSuccess, valid }}>
                    <input
                      id={id}
                      type={'text'}
                      name={name}
                      onChange={this.handleChange}
                      onBlur={onBlur}
                      required={!!required}
                      className={'form-control'}
                      aria-invalid={!valid}
                      aria-describedby={describedBy}
                      {...inputAttrsProps}
                      value={value}
                    />
                  </GlyphiconWrapper>

                  {appendAddon && <AddonWrapper className="input-group-addon">{appendAddon}</AddonWrapper>}

                  {appendButtons && (
                    <ButtonsWrapper className="input-group-btn">
                      {appendButtons.map((addon: any, idx: number) => React.cloneElement(addon, { key: idx }))}
                    </ButtonsWrapper>
                  )}
                </div>
              </FieldHelpersWrapper>
            </div>
          );
        }}
      </RegisterElement>
    );
  }

  private handleChange = (e: ChangeEvent<any>) => {
    const { formik, onChange, name } = this.props;
    const { value } = e.target;

    if (onChange) {
      onChange(name, value);
    } else if (formik && formik.handleChange) {
      formik.handleChange(e);
    }
  };
}

export default connect<IInputGroupProps>(InputGroup);
